import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClock,
  faPlay,
  faPause,
  faTrash,
  faEdit,
  faPlus,
  faSpinner,
  faCheckCircle,
  faExclamationCircle,
  faHistory,
  faRocket
} from '@fortawesome/free-solid-svg-icons';
import { api, getApiUrl } from '../utils/apiConfig';
import AutoPacingScheduleModal from './AutoPacingScheduleModal';
import AutoPacingExecutionHistory from './AutoPacingExecutionHistory';
import './AutoPacingScheduleList.css';

/**
 * AutoPacingScheduleList - Displays and manages auto pacing schedules for a campaign
 */
const AutoPacingScheduleList = ({ campaign, accountId, user }) => {
  const [schedules, setSchedules] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionInProgress, setActionInProgress] = useState(null);
  const [historySchedule, setHistorySchedule] = useState(null);

  // Fetch schedules
  const fetchSchedules = useCallback(async () => {
    if (!campaign?.id || !accountId) return;
    
    try {
      setIsLoading(true);
      const response = await api.get(
        getApiUrl(`/api/v2/campaigns/${campaign.id}/auto-pacing`),
        { params: { accountId, userId: user?.userId || user?.id } }
      );
      setSchedules(response.data.schedules || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching auto pacing schedules:', err);
      setError('Failed to load auto pacing schedules');
    } finally {
      setIsLoading(false);
    }
  }, [campaign?.id, accountId, user]);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  // Create schedule
  const handleCreateSchedule = async (scheduleData) => {
    try {
      setIsSubmitting(true);
      await api.post(
        getApiUrl(`/api/v2/campaigns/${campaign.id}/auto-pacing`),
        scheduleData,
        { params: { accountId, userId: user?.userId || user?.id } }
      );
      setShowModal(false);
      fetchSchedules();
    } catch (err) {
      console.error('Error creating schedule:', err);
      alert('Failed to create schedule: ' + (err.response?.data?.error || err.message));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Update schedule
  const handleUpdateSchedule = async (scheduleData) => {
    if (!editingSchedule) return;
    
    try {
      setIsSubmitting(true);
      await api.patch(
        getApiUrl(`/api/v2/campaigns/${campaign.id}/auto-pacing/${editingSchedule.id}`),
        scheduleData,
        { params: { accountId, userId: user?.userId || user?.id } }
      );
      setShowModal(false);
      setEditingSchedule(null);
      fetchSchedules();
    } catch (err) {
      console.error('Error updating schedule:', err);
      alert('Failed to update schedule: ' + (err.response?.data?.error || err.message));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete schedule
  const handleDeleteSchedule = async (scheduleId) => {
    if (!window.confirm('Are you sure you want to delete this auto pacing schedule?')) return;
    
    try {
      setActionInProgress(scheduleId);
      await api.delete(
        getApiUrl(`/api/v2/campaigns/${campaign.id}/auto-pacing/${scheduleId}`),
        { params: { accountId, userId: user?.userId || user?.id } }
      );
      fetchSchedules();
    } catch (err) {
      console.error('Error deleting schedule:', err);
      alert('Failed to delete schedule: ' + (err.response?.data?.error || err.message));
    } finally {
      setActionInProgress(null);
    }
  };

  // Toggle schedule status (pause/resume)
  const handleToggleStatus = async (schedule) => {
    const newStatus = schedule.status === 'active' ? 'paused' : 'active';
    
    try {
      setActionInProgress(schedule.id);
      await api.patch(
        getApiUrl(`/api/v2/campaigns/${campaign.id}/auto-pacing/${schedule.id}`),
        { status: newStatus },
        { params: { accountId, userId: user?.userId || user?.id } }
      );
      fetchSchedules();
    } catch (err) {
      console.error('Error toggling schedule status:', err);
      alert('Failed to update schedule: ' + (err.response?.data?.error || err.message));
    } finally {
      setActionInProgress(null);
    }
  };

  // Manually trigger execution
  const handleRunNow = async (scheduleId) => {
    try {
      setActionInProgress(scheduleId);
      await api.post(
        getApiUrl(`/api/v2/campaigns/${campaign.id}/auto-pacing/${scheduleId}/run`),
        {},
        { params: { accountId, userId: user?.userId || user?.id } }
      );
      alert('Auto pacing execution triggered successfully');
      fetchSchedules();
    } catch (err) {
      console.error('Error triggering execution:', err);
      alert('Failed to trigger execution: ' + (err.response?.data?.error || err.message));
    } finally {
      setActionInProgress(null);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { icon: faCheckCircle, color: '#10b981', label: 'Active' },
      paused: { icon: faPause, color: '#f59e0b', label: 'Paused' },
      error: { icon: faExclamationCircle, color: '#ef4444', label: 'Error' }
    };
    const config = statusConfig[status] || statusConfig.paused;
    return (
      <span className="status-badge" style={{ color: config.color }}>
        <FontAwesomeIcon icon={config.icon} /> {config.label}
      </span>
    );
  };

  const getDayName = (dayNum) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[dayNum] || 'Monday';
  };

  const getScheduleTypeLabel = (schedule) => {
    const type = schedule.scheduleType;
    if (type === 'weekly') {
      const dayName = getDayName(schedule.weeklyDay ?? 1);
      return `Weekly (${dayName}s)`;
    }
    const labels = {
      daily: 'Daily',
      hourly: 'Hourly',
      custom: 'Custom'
    };
    return labels[type] || type;
  };

  if (isLoading) {
    return (
      <div className="auto-pacing-section">
        <div className="section-header">
          <h3><FontAwesomeIcon icon={faClock} /> Auto Pacing</h3>
        </div>
        <div className="loading-state">
          <FontAwesomeIcon icon={faSpinner} spin /> Loading schedules...
        </div>
      </div>
    );
  }

  return (
    <div className="auto-pacing-section">
      <div className="section-header">
        <h3><FontAwesomeIcon icon={faClock} /> Auto Pacing</h3>
        <button
          className="btn btn-primary btn-sm"
          onClick={() => {
            setEditingSchedule(null);
            setShowModal(true);
          }}
        >
          <FontAwesomeIcon icon={faPlus} /> Add Schedule
        </button>
      </div>

      {error && (
        <div className="error-message">
          <FontAwesomeIcon icon={faExclamationCircle} /> {error}
        </div>
      )}

      {schedules.length === 0 ? (
        <div className="empty-state">
          <FontAwesomeIcon icon={faClock} size="2x" style={{ color: '#9ca3af', marginBottom: '8px' }} />
          <p>No auto pacing schedules configured</p>
          <p style={{ fontSize: '12px', color: '#6b7280' }}>
            Create a schedule to automatically optimize campaign budgets
          </p>
        </div>
      ) : (
        <div className="schedules-list">
          {schedules.map(schedule => (
            <div key={schedule.id} className="schedule-card">
              <div className="schedule-header">
                <div className="schedule-name">
                  <strong>{schedule.name}</strong>
                  {getStatusBadge(schedule.status)}
                </div>
                <div className="schedule-actions">
                  {actionInProgress === schedule.id ? (
                    <FontAwesomeIcon icon={faSpinner} spin />
                  ) : (
                    <>
                      <button
                        className="btn-icon"
                        onClick={() => setHistorySchedule(schedule)}
                        title="View History"
                      >
                        <FontAwesomeIcon icon={faHistory} />
                      </button>
                      <button
                        className="btn-icon"
                        onClick={() => handleRunNow(schedule.id)}
                        title="Run Now"
                        disabled={schedule.status !== 'active'}
                      >
                        <FontAwesomeIcon icon={faRocket} />
                      </button>
                      <button
                        className="btn-icon"
                        onClick={() => handleToggleStatus(schedule)}
                        title={schedule.status === 'active' ? 'Pause' : 'Resume'}
                      >
                        <FontAwesomeIcon icon={schedule.status === 'active' ? faPause : faPlay} />
                      </button>
                      <button
                        className="btn-icon"
                        onClick={() => {
                          setEditingSchedule(schedule);
                          setShowModal(true);
                        }}
                        title="Edit"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button
                        className="btn-icon btn-danger"
                        onClick={() => handleDeleteSchedule(schedule.id)}
                        title="Delete"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className="schedule-details">
                <div className="detail-row">
                  <span className="detail-label">Schedule:</span>
                  <span>{getScheduleTypeLabel(schedule)} at {schedule.executionTime?.substring(0, 5) || '09:00'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Timezone:</span>
                  <span>{schedule.timezone}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Max Change:</span>
                  <span>±{schedule.maxBudgetChangePercent}%</span>
                </div>
                {(schedule.minDailyBudget || schedule.maxDailyBudget) && (
                  <div className="detail-row">
                    <span className="detail-label">Budget Range:</span>
                    <span>
                      ${schedule.minDailyBudget || '0'} - ${schedule.maxDailyBudget || '∞'}
                    </span>
                  </div>
                )}
              </div>

              <div className="schedule-stats">
                <div className="stat">
                  <FontAwesomeIcon icon={faHistory} />
                  <span>{schedule.executionCount || 0} runs</span>
                </div>
                <div className="stat success">
                  <FontAwesomeIcon icon={faCheckCircle} />
                  <span>{schedule.successfulExecutions || 0}</span>
                </div>
                <div className="stat error">
                  <FontAwesomeIcon icon={faExclamationCircle} />
                  <span>{schedule.failedExecutions || 0}</span>
                </div>
                {schedule.nextExecutionAt && (
                  <div className="stat next-run">
                    <span>Next: {formatDateTime(schedule.nextExecutionAt)}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <AutoPacingScheduleModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingSchedule(null);
        }}
        campaign={campaign}
        schedule={editingSchedule}
        onSubmit={editingSchedule ? handleUpdateSchedule : handleCreateSchedule}
        isSubmitting={isSubmitting}
      />

      {/* Execution History Modal */}
      <AutoPacingExecutionHistory
        isOpen={!!historySchedule}
        onClose={() => setHistorySchedule(null)}
        schedule={historySchedule}
        campaign={campaign}
        accountId={accountId}
        user={user}
      />
    </div>
  );
};

export default AutoPacingScheduleList;

